import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import {
  Crown,
  LogOut,
  User,
  Shield,
  Home,
  Menu,
  Users,
  BarChart3,
  Settings,
} from "lucide-react";

import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { clearCsrfToken } from "@/lib/csrfClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { shutdownIntercom } from "@/lib/intercom";

interface SuperadminLayoutProps {
  children: React.ReactNode;
}

export default function SuperadminLayout({ children }: SuperadminLayoutProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/auth/logout");
    },
    onSuccess: () => {
      // Shutdown Intercom to clear user identity and chat history
      shutdownIntercom();

      clearCsrfToken();
      queryClient.clear();
      window.location.href = "/auth";
    },
    onError: () => {
      toast({
        title: "Logout Failed",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const getInitials = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    return "S";
  };

  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    return user?.email || "Superadmin";
  };

  const navigationItems = [
    {
      name: "Dashboard",
      href: "/admin",
      icon: BarChart3,
      current: window.location.pathname === "/admin",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="flex h-screen">
        {/* Fixed Hamburger Menu Button */}
        <div className="fixed top-4 left-4 z-50">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="bg-card border-border shadow-md hover:shadow-lg transition-shadow"
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>

        {/* Sidebar */}
        <div
          className={cn(
            "fixed top-0 left-0 h-full bg-card border-r border-border shadow-lg transition-all duration-300 z-40",
            sidebarOpen ? "w-64" : "w-0 overflow-hidden",
          )}
        >
          <div className="flex flex-col h-full pt-16">
            {/* Logo Section */}
            <div className="px-4 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-foreground">
                    TechAlign
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    Platform Management
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                    item.current
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </button>
              ))}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-border">
              {/* Superadmin Badge */}
              <div className="mb-4 px-3 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full border border-warning/20 flex items-center gap-1 w-fit">
                <Shield className="h-3 w-3" />
                Superadmin
              </div>

              {/* Return Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/")}
                className="w-full flex items-center gap-2 mb-3"
              >
                <Home className="h-4 w-4" />
                Return to Platform
              </Button>

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full flex items-center gap-2 p-2 justify-start"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.profileImageUrl ?? undefined} />
                      <AvatarFallback className="bg-blue-500 text-white text-sm">
                        {getInitials(user?.firstName ?? undefined, user?.lastName ?? undefined)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left">
                      <div className="text-sm font-medium text-foreground">
                        {getUserDisplayName()}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {user?.email}
                      </div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => logoutMutation.mutate()}
                    className="text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div
          className={cn(
            "flex-1 flex flex-col transition-all duration-300",
            sidebarOpen ? "ml-64" : "ml-0",
          )}
        >
          {/* Main Content */}
          <main className="flex-1 pt-16">{children}</main>

          {/* Footer */}
          <footer className="border-t border-border bg-card p-4">
            <div className="flex items-center justify-center">
              <div className="text-sm text-muted-foreground">
                © 2026 TechAlign - Platform Management Console
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
